//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { fetchTwitterSettings } from '@account-settings/redux/twitter/twitterSettingsSlice';
import { ITwitterUser } from '@apis/twitter-analytics/twitterAnalyticsApiModel';
import * as twitterUserApi from '@apis/twitter-user/twitterUserApi';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { ITwitterUserState } from '@twitterV2/redux/user/twitterUserModel';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

const initialState: ITwitterUserState = {
  user: undefined,
  flags: {
    isFetchingTwitterUser: false,
  },
  errors: {
    fetchTwitterUserError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.TwitterUser,
  initialState,
  reducers: {
    requestTwitterUser(state) {
      state.flags.isFetchingTwitterUser = true;
      state.errors.fetchTwitterUserError = undefined;
    },
    receiveTwitterUserSucceeded(
      state,
      action: PayloadAction<ITwitterUser | undefined>,
    ) {
      state.flags.isFetchingTwitterUser = false;
      state.user = action.payload;
    },
    receiveTwitterUserFailed(state, action: PayloadAction<string | undefined>) {
      state.flags.isFetchingTwitterUser = false;
      state.errors.fetchTwitterUserError = action.payload;
    },
  },
});

export const fetchUser = createThunk(
  (username: string) => async (dispatch, getState) => {
    const {
      flags: { isFetchingTwitterUser },
    } = getState().twitter.user;

    if (isFetchingTwitterUser) {
      return;
    }

    dispatch(actions.requestTwitterUser());

    try {
      const user = await twitterUserApi.getUser({
        twitterUsername: username,
      });

      dispatch(actions.receiveTwitterUserSucceeded(user));
    } catch (e) {
      dispatch(actions.receiveTwitterUserFailed(getRequestErrorMessage(e)));
    }
  },
);

export const fetchSettingsAndUser = createThunk(() => async (dispatch) => {
  const username = await dispatch(fetchTwitterSettings());

  if (isDefined(username)) {
    await dispatch(fetchUser(username));
  }
});

export default reducer;
